import { isPrivateTokenPath, redactAnalyticsValue } from "@/lib/analytics-privacy";
import type { User } from "@/types/models";
import * as Sentry from "@sentry/react";
import type { PostHog, Properties } from "posthog-js";

export const postHogFeatures = {
  documents: "documents",
  familyCollection: "family_collection",
  familySharing: "family_sharing",
  lineSummary: "line_summary",
  memberRequests: "member_requests",
  nodeExport: "node_export",
  nodeManagement: "node_management",
  notifications: "notifications",
  paperTree: "paper_tree",
  posts: "posts",
  relationshipAnalysis: "relationship_analysis",
  reports: "reports",
  treeMaintenance: "tree_maintenance",
  treePdfExport: "tree_pdf_export",
  treeSearch: "tree_search",
  tutorials: "tutorials",
} as const;

type AnalyticsProperty = string | number | boolean | null;
type PostHogFeature = (typeof postHogFeatures)[keyof typeof postHogFeatures];
type PostHogCall = (posthog: PostHog) => void;

const POSTHOG_PROJECT_KEY = "phc_CVPSRzgzGDrJZbYhLaMHm94BNxaQWdxBOiPe1yUr4HN";
// Bounds the queue if the SDK never loads, for example when its chunk fails to download.
const MAX_QUEUED_CALLS = 500;

let client: PostHog | null = null;
const queuedCalls: PostHogCall[] = [];

/** Runs the call on the started SDK, or queues it until `loadWebAnalytics` starts the SDK. */
const withPostHog = (call: PostHogCall) => {
  if (client) {
    call(client);
    return;
  }

  if (queuedCalls.length < MAX_QUEUED_CALLS) {
    queuedCalls.push(call);
  }
};

/** Captures now, or queues the event with the moment it happened, so a late SDK still records the right time. */
const capture = (event: string, properties: Properties) => {
  if (client) {
    client.capture(event, properties);
    return;
  }

  const timestamp = new Date();

  withPostHog((posthog) => posthog.capture(event, properties, { timestamp }));
};

/**
 * Downloads and starts PostHog outside the entry bundle, so it no longer delays the first render. Calls made before
 * it starts run afterwards in the order they were made, so no page view, identity, or event is lost.
 */
export const loadWebAnalytics = async (): Promise<void> => {
  const { default: posthog } = await import("@/lib/usage-sdk");

  posthog.init(POSTHOG_PROJECT_KEY, {
    api_host: "https://us.i.posthog.com",
    autocapture: false,
    capture_pageview: false,
    before_send: (event) => (event ? redactAnalyticsValue(event) : event),
    disable_session_recording: isPrivateTokenPath(window.location.pathname),
  });
  posthog.register({ actor_type: "anonymous", is_demo: false, surface: "web" });
  client = posthog;

  for (const call of queuedCalls.splice(0)) {
    try {
      call(posthog);
    } catch {
      // Analytics must never interrupt product behavior; one failing call must not drop the rest.
    }
  }
};

/**
 * Starts PostHog on the Tree Studio editor for its performance events only. The editor's panels show family names,
 * so replay, autocapture, rage clicks, heatmaps, surveys, remote config and page views stay off, nothing persists in
 * the browser, and events carry the Admin's account id without a person profile.
 */
export const loadTreeStudioAnalytics = async (adminId: number): Promise<void> => {
  const { default: posthog } = await import("@/lib/usage-sdk");

  posthog.init(POSTHOG_PROJECT_KEY, {
    api_host: "https://us.i.posthog.com",
    autocapture: false,
    rageclick: false,
    capture_pageview: false,
    capture_pageleave: false,
    capture_dead_clicks: false,
    capture_heatmaps: false,
    capture_exceptions: false,
    capture_performance: false,
    disable_session_recording: true,
    disable_surveys: true,
    disable_product_tours: true,
    disable_web_experiments: true,
    disable_external_dependency_loading: true,
    advanced_disable_flags: true,
    persistence: "memory",
    person_profiles: "never",
    bootstrap: { distinctID: `admin:${adminId}` },
    before_send: (event) => (event ? redactAnalyticsValue(event) : event),
  });
  posthog.register({ actor_type: "admin", is_demo: false, surface: "tree_studio" });
  client = posthog;

  for (const call of queuedCalls.splice(0)) {
    try {
      call(posthog);
    } catch {
      // Analytics must never interrupt the editor; one failing call must not drop the rest.
    }
  }
};

/** Tree Studio field performance; see docs/product-setup.md#posthog for the properties and their dashboard. */
export const captureTreeStudioPerformance = (
  event: "tree_studio_perf_summary" | "tree_studio_perf_slow",
  properties: Record<string, AnalyticsProperty>,
  { beacon = false } = {},
) => {
  try {
    if (beacon) {
      // A summary sent from pagehide must survive the page closing.
      withPostHog((posthog) => posthog.capture(event, properties, { transport: "sendBeacon" }));
    } else {
      capture(event, properties);
    }
  } catch {
    // Analytics must never interrupt the editor.
  }
};

const captureOncePerSession = (value: string, properties: Record<string, AnalyticsProperty>) => {
  const key = `awraq:feature_used:${value}`;

  try {
    if (sessionStorage.getItem(key)) {
      return;
    }

    sessionStorage.setItem(key, "1");
  } catch {
    // persistence-best-effort: Analytics must not block the product when storage is unavailable.
  }

  capture("feature_used", properties);
};

export const captureFeatureUsed = (feature: PostHogFeature, properties: Record<string, AnalyticsProperty> = {}) =>
  captureOncePerSession(feature, { feature, surface: "web", ...properties });

export type ContentShareMethod = "link" | "whatsapp" | "download";

/**
 * Records a shared photo or document. It carries only the content type and the method, never ids, titles or links.
 */
export const captureContentShared = (type: "photo" | "document", method: ContentShareMethod) => {
  try {
    capture("content_shared", { type, method, surface: "web" });
  } catch {
    // Analytics must never interrupt sharing.
  }
};

export const capturePageView = (component: string) => {
  const pageName = component.replace(/[^A-Za-z0-9/_-]/g, "");
  const pathname = `/app/${pageName}`;

  capture("$pageview", {
    $current_url: `${window.location.origin}${pathname}`,
    $pathname: pathname,
    page_name: pageName,
  });
};

/** Token pages hold the token in the URL, so a running replay stops before one is recorded. */
export const stopSessionRecording = () =>
  withPostHog((posthog) => {
    if (posthog.sessionRecordingStarted()) {
      posthog.stopSessionRecording();
    }
  });

export const identifyWebUser = (user: User) => {
  const distinctId = `user:${user.id}`;

  // Sentry gets the same account id as PostHog and nothing else (no email, phone, or name).
  Sentry.setUser({ id: distinctId });

  withPostHog((posthog) => {
    posthog.identify(distinctId, {
      actor_type: "user",
      is_demo: user.is_demo,
    });
    posthog.group("tenant", `tenant:${user.tenant_id}`);
    posthog.register({
      actor_type: "user",
      is_demo: user.is_demo,
      surface: "web",
    });
  });
};

export const resetWebAnalytics = () => {
  Sentry.setUser(null);

  withPostHog((posthog) => {
    try {
      posthog.reset();
      posthog.register({
        actor_type: "anonymous",
        is_demo: false,
        surface: "web",
      });
    } catch {
      // SDK extensions can access blocked storage. Analytics must not interrupt logout cleanup.
    }
  });
};
